import { useState, useMemo, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Plus, Download, Upload, Search, Eye, Pencil, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import ContactFormModal from './ContactFormModal';
import ContactViewModal from './ContactViewModal';

const TYPES = ['All Addresses', 'Customer', 'Shipper', 'Consignee', 'Driver', 'Maintenance Vendor', 'Carrier'];
const ITEMS_PER_PAGE = 100;

export default function AddressBookTab() {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All Addresses');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [viewContact, setViewContact] = useState(null);

  const loadContacts = async () => {
    setLoading(true);
    const data = await client.entities.Contact.list('-created_date', 5000);
    setContacts(data);
    setLoading(false);
  };

  useEffect(() => {
    loadContacts();
  }, []);

  const filteredContacts = useMemo(() => {
    // Only top-level Accounts show in the main list -- a row with
    // parent_contact_id is a person that belongs to one of these
    // accounts and only shows inside that account's detail view.
    let result = contacts.filter(c => !c.parent_contact_id);

    // Type filter
    if (filter !== 'All Addresses') {
      result = result.filter(c => c.type === filter);
    }

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        (c.name?.toLowerCase().includes(q)) ||
        (c.city?.toLowerCase().includes(q)) ||
        (c.primary_phone?.includes(q)) ||
        (c.email?.toLowerCase().includes(q))
      );
    }

    return result;
  }, [contacts, filter, search]);

  const totalPages = Math.ceil(filteredContacts.length / ITEMS_PER_PAGE);
  const start = (page - 1) * ITEMS_PER_PAGE;
  const end = start + ITEMS_PER_PAGE;
  const pageContacts = filteredContacts.slice(start, end);

  const handleExport = () => {
    const headers = ['name', 'street', 'city', 'state', 'zip_code', 'email', 'contact', 'primary_phone', 'alternate_phone', 'fax', 'motor_carrier_number', 'alternate_phone2', 'notes'];
    const csvHeader = headers.join(',');
    const csvRows = filteredContacts.map(c =>
      headers.map(h => {
        const val = c[h] || '';
        return `"${val.toString().replace(/"/g, '""')}"`;
      }).join(',')
    );
    const csv = [csvHeader, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `address-book-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) {
      toast.error('CSV file is empty or invalid.');
      return;
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const expectedHeaders = ['name', 'street', 'city', 'state', 'zip_code', 'email', 'contact', 'primary_phone', 'alternate_phone', 'fax', 'motor_carrier_number', 'alternate_phone2', 'notes'];
    
    const headerMap = {};
    for (let i = 0; i < headers.length; i++) {
      headerMap[expectedHeaders[i]] = i;
    }

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = [];
      let inQuotes = false;
      let cell = '';
      for (let j = 0; j < lines[i].length; j++) {
        const char = lines[i][j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          cells.push(cell.trim());
          cell = '';
        } else {
          cell += char;
        }
      }
      cells.push(cell.trim());

      const name = cells[headerMap['name']] || '';
      if (!name) continue; // Skip empty rows

      rows.push({
        name: name,
        street: cells[headerMap['street']] || '',
        city: cells[headerMap['city']] || '',
        state: cells[headerMap['state']] || '',
        zip_code: cells[headerMap['zip_code']] || '',
        email: cells[headerMap['email']] || '',
        contact: cells[headerMap['contact']] || '',
        primary_phone: cells[headerMap['primary_phone']] || '',
        alternate_phone: cells[headerMap['alternate_phone']] || '',
        fax: cells[headerMap['fax']] || '',
        motor_carrier_number: cells[headerMap['motor_carrier_number']] || '',
        alternate_phone2: cells[headerMap['alternate_phone2']] || '',
        notes: cells[headerMap['notes']] || '',
        type: 'Customer',
      });
    }

    if (rows.length === 0) {
      toast.error('No valid rows to import.');
      return;
    }

    toast.info(`Importing ${rows.length} contacts…`);
    await client.entities.Contact.bulkCreate(rows);
    toast.success(`${rows.length} contacts imported.`);
    loadContacts();
    setPage(1);
    e.target.value = '';
  };

  return (
    <div className="space-y-4">
      {/* Action bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button onClick={() => { setEditContact(null); setShowForm(true); }} className="bg-primary text-primary-foreground">
          <Plus className="w-4 h-4 mr-2" /> Add Address
        </Button>
        <Button onClick={handleExport} variant="outline" className="border-border">
          <Download className="w-4 h-4 mr-2" /> Export Spreadsheet
        </Button>
        <label>
          <input type="file" accept=".csv" onChange={handleImport} className="hidden" />
          <Button as="span" variant="outline" className="border-border cursor-pointer">
            <Upload className="w-4 h-4 mr-2" /> Import Address
          </Button>
        </label>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, city, phone, email…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto glass-card p-1.5 rounded-xl">
        {TYPES.map(t => (
          <button
            key={t}
            onClick={() => { setFilter(t); setPage(1); }}
            className={cn(
              "px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors",
              filter === t
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-white/10'
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="glass-card p-12 flex justify-center">
          <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
        </div>
      ) : pageContacts.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <p className="text-muted-foreground text-sm">No addresses found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium">Name</th>
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium">Email</th>
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium">Phone</th>
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium">Location</th>
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium">Contacts</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pageContacts.map(c => (
                    <tr key={c.id} className="hover:bg-white/3 transition-colors">
                      <td className="px-4 py-3 font-semibold text-foreground cursor-pointer hover:text-primary" onClick={() => setViewContact(c)}>
                        <span className="flex items-center gap-2">
                          {c.name}
                          {c.is_blacklisted && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-400">
                              BLACKLISTED
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {c.email ? (
                          <a href={`mailto:${c.email}`} className="text-blue-400 hover:text-blue-300">
                            {c.email}
                          </a>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{c.primary_phone || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{c.city && c.state ? `${c.city}, ${c.state}` : '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {contacts.filter(x => x.parent_contact_id === c.id).length || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={() => setViewContact(c)}
                            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
                            title="View"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => { setEditContact(c); setShowForm(true); }}
                            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 text-sm text-muted-foreground">
            <span>
              {filteredContacts.length === 0 ? '0' : start + 1} - {Math.min(end, filteredContacts.length)} of {filteredContacts.length}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-border h-8 px-3"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-border h-8 px-3"
                disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <ContactFormModal
          contact={editContact}
          onClose={() => { setShowForm(false); setEditContact(null); }}
          onSaved={() => { loadContacts(); setShowForm(false); setEditContact(null); }}
        />
      )}

      {viewContact && (
        <ContactViewModal
          contact={viewContact}
          contacts={contacts}
          onClose={() => setViewContact(null)}
          onEdit={() => { setEditContact(viewContact); setShowForm(true); setViewContact(null); }}
          onContactsChanged={loadContacts}
        />
      )}
    </div>
  );
}